import React, { useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

type AnchorPosition = "top" | "bottom" | "left" | "right";

interface PopupProps {
  show: boolean;
  children: React.ReactNode;
  popupContent: React.ReactNode;
  position: AnchorPosition;
  className?: string;
  offset?: number;
  onDismiss: () => void;
}

export const Popup: React.FC<PopupProps> = ({
  show,
  children,
  popupContent,
  position: anchor,
  className = "",
  offset = 16,
  onDismiss,
}) => {
  const childRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!show || !childRef.current || !popupRef.current) return;

    const updatePosition = () => {
      const childRect = childRef.current!.getBoundingClientRect();
      const popupRect = popupRef.current!.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const calculatePosition = (anchor: AnchorPosition) => {
        switch (anchor) {
          case "top":
            return {
              top: childRect.top - popupRect.height - offset,
              left: childRect.left + (childRect.width - popupRect.width) / 2,
            };
          case "bottom":
            return {
              top: childRect.bottom + offset,
              left: childRect.left + (childRect.width - popupRect.width) / 2,
            };
          case "left":
            return {
              top: childRect.top + (childRect.height - popupRect.height) / 2,
              left: childRect.left - popupRect.width - offset,
            };
          case "right":
            return {
              top: childRect.top + (childRect.height - popupRect.height) / 2,
              left: childRect.right + offset,
            };
        }
      };

      const basePosition = calculatePosition(anchor);

      // Apply viewport boundary constraints
      const constrainedPosition = {
        left: Math.max(
          8,
          Math.min(basePosition.left, viewport.width - popupRect.width - 8),
        ),
        top: Math.max(
          8,
          Math.min(basePosition.top, viewport.height - popupRect.height - 8),
        ),
      };

      setPosition(constrainedPosition);
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [show, anchor, offset]);

  // Handle background click dismissal
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't dismiss if clicking on the anchor element or popup content
      if (
        childRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      ) {
        return;
      }

      onDismiss?.();
    };

    // Use capture phase to handle the event before it bubbles
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [show, onDismiss]);

  return (
    <>
      <div ref={childRef} style={{ display: "inline-block" }}>
        {children}
      </div>
      {show &&
        createPortal(
          <div
            ref={popupRef}
            className={`popup ${className}`}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              zIndex: 1000,
              backgroundColor: "white",
              borderRadius: "4px",
              boxShadow: "1px 1px 12px 0px rgba(0, 0, 0, 0.25)",
            }}
          >
            {popupContent}
          </div>,
          document.body,
        )}
    </>
  );
};
